import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * Check if the application is running inside a native mobile wrapper (iOS / Android)
 */
export const isNativeApp = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if the application is running inside native iOS
 */
export const isNativeIOS = () => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
};

/**
 * Get current platform ('ios', 'android', 'web')
 */
export const getPlatform = () => {
  return Capacitor.getPlatform();
};

/**
 * Trigger tactile haptic feedback (Apple Taptic Engine on iOS)
 * @param {'light' | 'medium' | 'heavy'} style
 */
export const triggerHaptic = async (style = 'light') => {
  try {
    if (Capacitor.isPluginAvailable('Haptics')) {
      let impactStyle = ImpactStyle.Light;
      if (style === 'medium') impactStyle = ImpactStyle.Medium;
      if (style === 'heavy') impactStyle = ImpactStyle.Heavy;
      await Haptics.impact({ style: impactStyle });
    } else if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
      // Browser fallback
      const duration = style === 'heavy' ? 25 : style === 'medium' ? 15 : 8;
      window.navigator.vibrate(duration);
    }
  } catch (e) {
    // Graceful fallback if haptics are not supported or rejected
  }
};

/**
 * Trigger notification haptics (success / warning / error)
 * @param {'success' | 'warning' | 'error'} type
 */
export const triggerNotificationHaptic = async (type = 'success') => {
  try {
    if (Capacitor.isPluginAvailable('Haptics')) {
      let notifType = NotificationType.Success;
      if (type === 'warning') notifType = NotificationType.Warning;
      if (type === 'error') notifType = NotificationType.Error;
      await Haptics.notification({ type: notifType });
    } else if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
      if (type === 'success') window.navigator.vibrate([10, 50, 10]);
      if (type === 'error') window.navigator.vibrate([20, 50, 20, 50, 20]);
    }
  } catch (e) {
    // Graceful fallback
  }
};

/**
 * Synchronize iOS / PWA status bar with light / dark theme
 * @param {boolean} isDark
 */
export const syncStatusBar = (isDark = true) => {
  try {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#0B0F17' : '#F8FAFC');
    }
  } catch (e) {
    // Graceful fallback
  }
};
